import { Component, Signal, WritableSignal, computed, inject, input, output, signal } from '@angular/core';
import { ControlValueAccessor, NgControl, ValidationErrors, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FORM_INPUT_VALIDATION_MESSAGES } from '@settings/form-validation-settings';
import { IonInput, IonLabel } from '@ionic/angular/standalone';
import { MbmFormErrorComponent } from '@components/forms/mbm-form-error/mbm-form-error.component';

@Component({
  selector: 'mbm-textbox',
  imports: [TranslateModule, IonInput, IonLabel, MbmFormErrorComponent],
  templateUrl: './mbm-textbox.component.html',
  styleUrl: './mbm-textbox.component.scss',
})
export class MbmTextboxComponent implements ControlValueAccessor {
  readonly type = input<'text' | 'email' | 'password' | 'number' | 'tel' | 'url'>('text');
  readonly placeholderKey = input<string>();
  readonly labelKey = input<string>();
  readonly iconSrc = input<string>();
  readonly disabled = input<boolean>(false);
  readonly required = input<boolean>(false);
  readonly showRequiredIndicator = input<boolean>(true);
  readonly errorMessages = input<Record<string, string> | undefined>(undefined);

  readonly valueChange = output<string>();

  readonly isRequired: Signal<boolean> = computed(() => {
    this.internalValue(); // reactive dependency
    return this.required() || (this.ngControl?.control?.hasValidator(Validators.required) ?? false);
  });

  readonly errorMessage: Signal<string | undefined> = computed(() => this.getError());
  protected readonly internalValue: WritableSignal<string> = signal('');
  protected readonly isDisabled: Signal<boolean> = computed(() => this.disabled() || this.disabledByForm());

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private readonly disabledByForm: WritableSignal<boolean> = signal(false);
  private readonly translate = inject(TranslateService);
  private readonly ngControl: NgControl | undefined = inject(NgControl, { self: true, optional: true }) ?? undefined;

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: string): void {
    this.internalValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const next = (event as CustomEvent<{ value: string | null | undefined }>).detail.value ?? '';
    this.internalValue.set(next);
    this.onChange(next);
    this.valueChange.emit(next);
  }

  protected onBlur(): void {
    this.onTouched();
  }

  private showError(): boolean {
    if (!this.ngControl?.control) return false;
    return this.ngControl.control.invalid && (this.ngControl.control.dirty || this.ngControl.control.touched);
  }

  private getError(): string | undefined {
    this.internalValue(); // reactive dependency
    if (!this.showError()) return undefined;
    const errors: ValidationErrors | null = this.ngControl?.control?.errors ?? null;
    if (!errors) return undefined;
    for (const key of Object.keys(errors)) {
      const translated = this.resolveErrorKey(key, errors[key]);
      if (translated) return translated;
    }
    return this.translate.instant('form.validation.errors.invalid');
  }

  private resolveErrorKey(key: string, error: unknown): string | undefined {
    const translationKey = this.errorMessages()?.[key] ?? FORM_INPUT_VALIDATION_MESSAGES[key];
    if (!translationKey) return undefined;
    if ((key === 'maxlength' || key === 'minlength') && typeof error === 'object' && error !== null) {
      const len = (error as { requiredLength?: number }).requiredLength;
      return this.translate.instant(translationKey, len != null ? { count: len } : {});
    }
    return this.translate.instant(translationKey);
  }
}
